import evessioAPI from "@/services/api/evessioAPI";
import { TABLE_TYPES } from "@/helpers/constants";
import awsRestAPI from "@/services/api/awsRestAPI";

export default {
  /**
   * Add a new table info in the current room context
   * This action will be called when somebody create a new table and send "tableCreation" notify via WebSocket
   * @return {Promise<void>}
   */
  async addTable({ commit, getters }, { tableUUID }) {
    if (getters.tables().find(t => t.uuid === tableUUID)) {
      return;
    }

    let response = await evessioAPI.getContentNodeContext({
      uuid: tableUUID,
      includeChildren: 0,
      exclude: null
    });

    commit("addTableToRoom", { table: response.data }, { root: true });

    return response.data;
  },

  /**
   * Get Or Create DigitalEventTable by user domain
   * @param commit {Function}
   * @param getters {Object}
   * @param dispatch
   * @return {Promise<{}>}
   */
  async getOrCreateUserTable({ commit, getters, dispatch, rootGetters }) {
    let userTable = getters.userTable;
    if (userTable) {
      // Update table
      if (!userTable.HAS_USERS) {
        userTable = await dispatch("updateTable", { has_users: true });
        await dispatch(
          "socket/createWebSocketConnection",
          {
            name: "tableCreationNotify",
            params: { tableUUID: userTable.uuid }
          },
          { root: true }
        );
      } else {
        await dispatch("socket/createWebSocketConnection", null, { root: true });
      }
      // Update assignment
      if (!rootGetters["users/userAssignment"].locked) {
        await dispatch(
          "room/updateAssignment",
          {
            assignmentId: rootGetters["users/userAssignment"].id,
            data: { locked: true }
          },
          { root: true }
        );
      }

      return userTable;
    }
    let response = await evessioAPI.createTable({
      tableData: {
        room_id: rootGetters["room/activeRoom"].id
      }
    });
    await dispatch("changeUserTable", {
      destinationTableUUID: response.data.TABLE_UUID
    });
    if (!getters.tables().find(t => t.uuid === response.data.uuid)) {
      commit(
        "addTableToRoom",
        {
          table: response.data,
          activeRoom: rootGetters["room/activeRoom"]
        },
        { root: true }
      );
    } else {
      commit(
        "updateTable",
        {
          tableUUID: response.data.uuid,
          data: response.data,
          activeRoom: rootGetters["room/activeRoom"]
        },
        { root: true }
      );
    }

    await dispatch("room/getRoomAssignments", null, { root: true });

    await dispatch(
      "socket/createWebSocketConnection",
      {
        name: "tableCreationNotify",
        params: { tableUUID: response.data.uuid }
      },
      { root: true }
    );

    return response.data;
  },

  /**
   * Update DigitalEventTable
   * @param commit {Function}
   * @param getters {Object}
   * @param data {Object} Table params for updating
   * @return {Promise<void>}
   */
  async updateTable({ commit, getters, rootGetters }, data) {
    let response = await evessioAPI.updateTable({
      tableUUID: getters.userTable.TABLE_UUID,
      data: data
    });

    commit(
      "updateTable",
      {
        tableUUID: getters.userTable.uuid,
        data: response.data,
        activeRoom: rootGetters["room/activeRoom"]
      },
      { root: true }
    );

    return response.data;
  },

  /**
   * Refresh table info
   * @param commit {Function}
   * @param getters {Object}
   * @param dispatch {Function}
   * @param rootGetters
   * @param rootState
   * @param tableUUID {String} ContentNode.uuid
   * @return {Promise<void>}
   */
  async refreshTable({ commit, getters, dispatch, rootGetters, rootState }, { tableUUID }) {
    let response = await evessioAPI.getContentNodeContext({
      uuid: tableUUID,
      includeChildren: 0,
      exclude: null
    });

    if (!getters.tables().find(t => t.uuid === tableUUID)) {
      commit("addTableToRoom", { table: response.data }, { root: true });
    } else {
      commit("updateTable", { tableUUID: tableUUID, data: response.data }, { root: true });
    }

    let oldTableUUID = getters.userTable.uuid;
    let newTableUUID = response.data.uuid;

    let userEmail = rootState.users.user.user_email.toLowerCase();

    if (
      (response.data.ASSIGNMENTS || []).includes(userEmail) &&
      rootGetters["users/userAssignment"].value === userEmail
    ) {
      /**
       * This case means that user was logged in via domain and after that EA set email-assignment to this user in
       * another table
       * So, we need to change user's table
       */
      await dispatch("changeUserTable", {
        destinationTableUUID: response.data.TABLE_UUID
      });

      if (!rootGetters["users/userAssignment"].locked) {
        await dispatch(
          "room/updateAssignment",
          {
            assignmentId: rootGetters["users/userAssignment"].id,
            data: { locked: true }
          },
          { root: true }
        );
      }

      if (oldTableUUID !== newTableUUID) {
        await dispatch("chat/getTableMessages", null, { root: true });
      }
    }

    if (response.data.TABLE_TYPE === TABLE_TYPES.PUBLIC_GALLERY) {
      await dispatch("chat/resetAllUnreadMessageCounter", null, { root: true });
    }

    return response.data;
  },

  /**
   * Get table users for activeRoom and userTable
   * @param getters {Object}
   * @return {Promise<{data: {}}>}
   */
  async getTableState({ getters }) {
    let tableUUID = getters.userTable.uuid;
    let response = await awsRestAPI.getTableState({ tableUUID: tableUUID });

    return response.data;
  },

  /**
   * Change UserTable
   * @param getters {Object}
   * @param commit {Function}
   * @param destinationTableUUID {String}
   * @return {Promise<void>}
   */
  async changeUserTable({ dispatch }, { destinationTableUUID }) {
    await dispatch("socket/stopStatePulling", null, { root: true });

    let response = await evessioAPI.changeUserTable({
      destination_table_uuid: destinationTableUUID
    });

    await dispatch("socket/startStatePulling", null, { root: true });

    await dispatch("room/getRoomAssignments", null, { root: true });

    return response.data;
  }
};
